import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import IntelxPanel from '../components/IntelxPanel';

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createUserError, setCreateUserError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showUrlSearch, setShowUrlSearch] = useState(false);
  const [showSlugSearch, setShowSlugSearch] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', confirmPassword: '', userType: 1 });
  const [searchDomain, setSearchDomain] = useState('');
  const [searchUrl, setSearchUrl] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [urlSearchLoading, setUrlSearchLoading] = useState(false);
  const [searchSlug, setSearchSlug] = useState('');
  const [slugSearchLoading, setSlugSearchLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const tableRef = useRef(null);

  useEffect(() => {
    if (!user || user['user_type'] !== 2) {
      navigate('/dashboard', { replace: true });
    } else {
      fetchUsers();
    }
    // eslint-disable-next-line
  }, [user]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    setSuccess('');
    try {
      const data = await api.getAllUsers();
      const sortedUsers = data.users.sort((a, b) => a.id - b.id);
      setUsers(sortedUsers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };



  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    setLoading(true);
    setError(null);
    setSuccess('');
    
    try {
      await api.deleteUser(userId);
      setSuccess('User deleted successfully');
      fetchUsers();
    } catch (err) {
      console.error('Delete user error:', err);
      if (err.message.includes('User not found')) {
        setError('User not found. It may have already been deleted.');
        fetchUsers(); // Refresh the list
      } else if (err.message.includes('Failed to delete user')) {
        setError('Failed to delete user. Please try again or contact support if the problem persists.');
      } else {
        setError(`Failed to delete user: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (userId) => {
    navigate('/admin/email', { state: { userId } });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateUserError(null);
    setSuccess('');
    setLoading(true);
    
    try {
      await api.createUser(newUser.email, newUser.password, newUser.confirmPassword, newUser.userType);
      setShowCreate(false);
      setNewUser({ email: '', password: '', confirmPassword: '', userType: 1 });
      setSuccess('User created successfully');
      fetchUsers();
    } catch (err) {
      console.error('Create user error:', err);
      setCreateUserError(err.message || 'Failed to create user. Please try again or contact support if the problem persists.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchHistory = async (e) => {
    e.preventDefault();
    if (!searchDomain.trim()) {
      setError('Please enter a domain name');
      return;
    }
    
    setError(null);
    setSearchLoading(true);
    
    try {
      await api.searchHistory(searchDomain.trim());
      setSuccess('History search started successfully');
      setShowSearch(false);
      setSearchDomain('');
    } catch (err) {
      if (err.message === 'Domain already being searched') {
        setError('Domain already being searched');
      } else {
        setError(err.message);
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchUrlHistory = async (e) => {
    e.preventDefault();
    if (!searchUrl.trim()) {
      setError('Please enter a URL');
      return;
    }
    
    setError(null);
    setUrlSearchLoading(true);
    
    try {
      await api.searchUrlHistory(searchUrl.trim());
      setSuccess('URL search started successfully');
      setShowUrlSearch(false);
      setSearchUrl('');
    } catch (err) {
      if (err.message === 'URL already being searched') {
        setError('URL already being searched');
      } else {
        setError(err.message);
      }
    } finally {
      setUrlSearchLoading(false);
    }
  };

  const handleSearchSlugHistory = async (e) => {
    e.preventDefault();
    if (!searchSlug.trim()) {
      setError('Please enter a slug');
      return;
    }

    setError(null);
    setSlugSearchLoading(true);

    try {
      await api.searchSlugHistory(searchSlug.trim());
      setSuccess('Slug search started successfully');
      setShowSlugSearch(false);
      setSearchSlug('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSlugSearchLoading(false);
    }
  };

  // Modal close handler
  const handleModalClose = (e) => {
    if (e.target.classList.contains('admin-modal-backdrop')) {
      setShowCreate(false);
      setShowSearch(false);
      setShowUrlSearch(false);
      setShowSlugSearch(false);
      setError(null);
      setCreateUserError(null);
      setSuccess('');
    }
  };

  return (
    <div className="container" style={{ maxWidth: 900, marginTop: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 className="mb-2" style={{ color: 'var(--text)', fontWeight: 700, marginBottom: 0 }}>Admin Dashboard</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn btn-link admin-search-btn"
            style={{ 
              fontSize: 14, 
              padding: '6px 12px', 
              color: 'var(--text)', 
              border: '1px solid var(--border)', 
              borderRadius: 6, 
              background: 'transparent',
              boxShadow: 'none', 
              transition: 'all 0.18s',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
            onClick={() => {
              setShowSearch(!showSearch);
              if (!showSearch) { setShowUrlSearch(false); setShowSlugSearch(false); }
            }}
          >
            {showSearch ? 'Cancel' : 'Search Domain History'}
          </button>
          <button
            className="btn btn-link admin-url-search-btn"
            style={{ 
              fontSize: 14, 
              padding: '6px 12px', 
              color: 'var(--text)', 
              border: '1px solid var(--border)', 
              borderRadius: 6, 
              background: 'transparent',
              boxShadow: 'none', 
              transition: 'all 0.18s',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
            onClick={() => {
              setShowUrlSearch(!showUrlSearch);
              if (!showUrlSearch) { setShowSearch(false); setShowSlugSearch(false); }
            }}
          >
            {showUrlSearch ? 'Cancel' : 'Search URL'}
          </button>
          <button
            className="btn btn-link admin-slug-search-btn"
            style={{ 
              fontSize: 14, 
              padding: '6px 12px', 
              color: 'var(--text)', 
              border: '1px solid var(--border)', 
              borderRadius: 6, 
              background: 'transparent',
              boxShadow: 'none', 
              transition: 'all 0.18s',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
            onClick={() => {
              setShowSlugSearch(!showSlugSearch);
              if (!showSlugSearch) { setShowSearch(false); setShowUrlSearch(false); }
            }}
          >
            {showSlugSearch ? 'Cancel' : 'Search Slugs'}
          </button>
          <button
            className="btn btn-link admin-create-btn"
            style={{ 
              fontSize: 14, 
              padding: '6px 12px', 
              color: 'var(--secondary-color)', 
              border: 'none', 
              borderRadius: 6, 
              background: 'var(--primary-color)', 
              boxShadow: 'none', 
              transition: 'all 0.18s',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
            onClick={() => setShowCreate(!showCreate)}
          >
            {showCreate ? 'Cancel' : '+ Create User'}
          </button>
        </div>
      </div>
       {success && <div className="success-message mb-3">{success}</div>}

      <IntelxPanel />
      
             {/* Domain History Search Modal */}
       {showSearch && (
         <div className="admin-modal-backdrop" onClick={handleModalClose}>
           <div
             className="admin-search-form"
             style={{ width: '100%', maxWidth: 400, margin: '0 auto', background: 'var(--white)', borderRadius: 10, boxShadow: '0 4px 24px rgba(30,39,56,0.13)', padding: '18px 24px' }}
           >
             <h3 style={{ marginBottom: 12, color: 'var(--text)', fontWeight: 600, fontSize: '1.15rem' }}>Search Domain</h3>
             <form onSubmit={handleSearchHistory}>
               <div className="form-group mb-3">
                 <label htmlFor="searchDomain" className="mb-1">Domain Name</label>
                 <input
                   id="searchDomain"
                   type="text"
                   className="form-input"
                   placeholder="Enter domain name (e.g., example.com)"
                   value={searchDomain}
                   onChange={e => setSearchDomain(e.target.value)}
                   required
                 />
               </div>
                               {error && <div className="error-message mb-3" style={{ fontSize: '0.875rem', padding: '6px 10px' }}>{error}</div>}
               <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                 <button type="button" className="btn btn-link" style={{ textDecoration: 'none' }} onClick={() => {
                   setShowSearch(false);
                   setError(null); // Clear error when canceling
                 }}>
                   Cancel
                 </button>
                 <button 
                   type="submit" 
                   className="btn btn-primary" 
                   style={{ background: 'var(--primary-color)', color: 'var(--secondary-color)' }}
                   disabled={searchLoading}
                 >
                   {searchLoading ? 'Searching...' : 'Search'}
                 </button>
               </div>
             </form>
           </div>
         </div>
       )}

      {/* URL History Search Modal */}
      {showUrlSearch && (
        <div className="admin-modal-backdrop" onClick={handleModalClose}>
          <div
            className="admin-search-form"
            style={{ width: '100%', maxWidth: 400, margin: '0 auto', background: 'var(--white)', borderRadius: 10, boxShadow: '0 4px 24px rgba(30,39,56,0.13)', padding: '18px 24px' }}
          >
            <h3 style={{ marginBottom: 12, color: 'var(--text)', fontWeight: 600, fontSize: '1.15rem' }}>Search URL</h3>
            <form onSubmit={handleSearchUrlHistory}>
              <div className="form-group mb-3">
                <label htmlFor="searchUrl" className="mb-1">URL</label>
                <input
                  id="searchUrl"
                  type="text"
                  className="form-input"
                  placeholder="Enter URL (e.g., example.com)"
                  value={searchUrl}
                  onChange={e => setSearchUrl(e.target.value)}
                  required
                />
              </div>
              {error && <div className="error-message mb-3" style={{ fontSize: '0.875rem', padding: '6px 10px' }}>{error}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn btn-link" style={{ textDecoration: 'none' }} onClick={() => {
                  setShowUrlSearch(false);
                  setError(null); // Clear error when canceling
                }}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  style={{ background: 'var(--primary-color)', color: 'var(--secondary-color)' }}
                  disabled={urlSearchLoading}
                >
                  {urlSearchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Slug Search Modal */}
      {showSlugSearch && (
        <div className="admin-modal-backdrop" onClick={handleModalClose}>
          <div
            className="admin-search-form"
            style={{ width: '100%', maxWidth: 400, margin: '0 auto', background: 'var(--white)', borderRadius: 10, boxShadow: '0 4px 24px rgba(30,39,56,0.13)', padding: '18px 24px' }}
          >
            <h3 style={{ marginBottom: 12, color: 'var(--text)', fontWeight: 600, fontSize: '1.15rem' }}>Search Slug</h3>
            <form onSubmit={handleSearchSlugHistory}>
              <div className="form-group mb-3">
                <label htmlFor="searchSlug" className="mb-1">Slug</label>
                <input
                  id="searchSlug"
                  type="text"
                  className="form-input"
                  placeholder="Slug"
                  value={searchSlug}
                  onChange={e => setSearchSlug(e.target.value)}
                  required
                />
              </div>
              {error && <div className="error-message mb-3" style={{ fontSize: '0.875rem', padding: '6px 10px' }}>{error}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn btn-link" style={{ textDecoration: 'none' }} onClick={() => {
                  setShowSlugSearch(false);
                  setError(null);
                }}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  style={{ background: 'var(--primary-color)', color: 'var(--secondary-color)' }}
                  disabled={slugSearchLoading}
                >
                  {slugSearchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreate && (
        <div className="admin-modal-backdrop" onClick={handleModalClose}>
            <form
              onSubmit={handleCreate}
              className="admin-create-form"
              style={{ width: '100%', maxWidth: 420, margin: '0 auto', background: 'var(--white)', borderRadius: 10, boxShadow: '0 4px 24px rgba(30,39,56,0.13)', padding: '18px 24px' }}
            >
              <h3 style={{ marginBottom: 12, color: 'var(--text)', fontWeight: 600, fontSize: '1.15rem' }}>Create New User</h3>
              {createUserError && (
                <div className="error-message mb-3" style={{ fontSize: '0.875rem', padding: '6px 10px' }}>
                  {createUserError}
                </div>
              )}
              <div className="form-group mb-2">
                <label htmlFor="email" className="mb-1">Email</label>
                <input
                  id="email"
                  type="email"
                  className="form-input"
                  placeholder="Email"
                  value={newUser.email}
                  onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group mb-2">
                <label htmlFor="password" className="mb-1">Password</label>
                <input
                  id="password"
                  type="password"
                  className="form-input"
                  placeholder="Password"
                  value={newUser.password}
                  onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                  required
                />
              </div>
              <div className="form-group mb-2">
                <label htmlFor="confirmPassword" className="mb-1">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  className="form-input"
                  placeholder="Confirm Password"
                  value={newUser.confirmPassword}
                  onChange={e => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                  required
                />
              </div>
              <div className="form-group mb-3">
                <label htmlFor="userType" className="mb-1">Role</label>
                <select
                  id="userType"
                  className="form-input"
                  value={newUser.userType}
                  onChange={e => setNewUser({ ...newUser, userType: parseInt(e.target.value) })}
                >
                  <option value={1}>User</option>
                  <option value={2}>Admin</option>
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn btn-link" style={{ textDecoration: 'none' }} onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ background: 'var(--primary-color)', color: 'var(--secondary-color)' }}>Create</button>
              </div>
            </form>
        </div>
      )}
      {loading ? (
        <p>Loading users...</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table
            ref={tableRef}
            style={{
              width: '100%',
              marginTop: '1em',
              borderCollapse: 'separate',
              borderSpacing: 0,
              background: 'var(--white)',
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(30,39,56,0.07)',
              overflow: 'hidden',
              minWidth: 600
            }}
          >
            <thead style={{ background: 'var(--secondary-color)', color: 'white' }}>
              <tr>
                <th style={{ padding: '12px 8px', fontWeight: 600 }}>ID</th>
                <th style={{ padding: '12px 8px', fontWeight: 600 }}>Email</th>
                <th style={{ padding: '12px 8px', fontWeight: 600 }}>Role</th>
                <th style={{ padding: '12px 8px', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => (
                <tr
                  key={u['id']}
                  style={{
                    background: idx % 2 === 0 ? 'var(--background)' : 'var(--white)',
                    borderBottom: '1px solid var(--border)'
                  }}
                >
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{u['id']}</td>
                  <td style={{ padding: '10px 8px' }}>{u['email']}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{u['user_type'] === 2 ? 'Admin' : 'User'}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                    <button
                      className="btn btn-link"
                      style={{ color: 'var(--text)', fontSize: 18 }}
                      title="Edit User"
                      onClick={() => handleEdit(u['id'])}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn btn-link"
                      style={{ color: 'var(--error)', fontSize: 18, marginLeft: 8 }}
                      title="Delete User"
                      onClick={() => handleDelete(u['id'])}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Admin; 